<template>
  <v-card height="100%" flat color="bg1" class="jira-version-picker overflow-y-auto">
    <!-- Page Header -->
    <v-list-item two-line :class="$vuetify.theme.isDark ? 'bg2' : 'white'">
      <v-list-item-content>
        <v-list-item-title class="title">New Release</v-list-item-title>
        <v-list-item-subtitle>
          Pick the Jira version you are shipping, choose which tickets customers should hear about,
          then let the AI write the notes.
        </v-list-item-subtitle>
      </v-list-item-content>
      <v-list-item-action class="flex-row align-center" style="gap: 8px">
        <v-btn text small @click="backToList">
          <v-icon left small>mdi-arrow-left</v-icon>
          Back to list
        </v-btn>
        <v-btn small outlined color="primary" @click="manualDialog = true">
          <v-icon left small>mdi-file-document-plus-outline</v-icon>
          Or start from scratch
        </v-btn>
      </v-list-item-action>
    </v-list-item>
    <v-divider></v-divider>

    <release-notes-disabled-notice v-if="featureDisabled" />

    <v-stepper v-else v-model="step" flat non-linear class="jira-version-picker__stepper bg1">
      <v-stepper-header class="elevation-0">
        <v-stepper-step :complete="!!selectedVersion" :editable="true" step="1">
          Pick a Jira version
          <small v-if="selectedVersion" class="mt-1">{{ selectedVersion.name }}</small>
        </v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step :editable="!!selectedVersion" step="2">
          Choose tickets
          <small v-if="selectedVersion" class="mt-1">{{ selectedKeys.length }} selected</small>
        </v-stepper-step>
      </v-stepper-header>

      <v-stepper-items>
        <!-- ------------------------------------------------ Step 1: version -->
        <v-stepper-content step="1" class="pa-0">
          <v-sheet class="ma-4 pa-3" outlined rounded>
            <div class="d-flex align-center flex-wrap" style="gap: 12px">
              <v-select
                v-model="projectKey"
                outlined
                dense
                hide-details
                label="Jira project"
                :item-text="projectText"
                item-value="key"
                style="max-width: 280px"
                :items="projects"
                :loading="projectsLoading"
                :disabled="projectsLoading"></v-select>

              <v-btn-toggle v-model="statusFilter" mandatory dense color="primary">
                <v-btn small value="released">Released</v-btn>
                <v-btn small value="unreleased">Unreleased</v-btn>
                <v-btn small value="all">All</v-btn>
              </v-btn-toggle>

              <v-spacer v-if="!$vuetify.breakpoint.xs"></v-spacer>

              <v-text-field
                v-model="searchInput"
                outlined
                dense
                hide-details
                clearable
                placeholder="Search versions..."
                prepend-inner-icon="mdi-magnify"
                style="max-width: 280px"></v-text-field>
            </div>
          </v-sheet>

          <v-sheet class="ma-4" outlined rounded>
            <version-list
              :versions="versions"
              :loading="versionsLoading"
              :total="versionsTotal"
              :options="options"
              @update:options="onOptionsUpdate"
              @select="onVersionSelected"
              @open-existing="goToRelease" />
          </v-sheet>
        </v-stepper-content>

        <!-- ------------------------------------------------- Step 2: tickets -->
        <v-stepper-content step="2" class="pa-0">
          <v-alert v-if="duplicateReleaseId" type="warning" text class="ma-4 mb-0">
            <div class="d-flex align-center flex-wrap" style="gap: 12px">
              <span>{{ duplicateMessage }}</span>
              <v-spacer></v-spacer>
              <v-btn small color="warning" @click="goToRelease(duplicateReleaseId)">
                Open that release
                <v-icon right small>mdi-arrow-right</v-icon>
              </v-btn>
            </div>
          </v-alert>

          <v-sheet class="ma-4 pa-3" outlined rounded>
            <div class="d-flex align-center flex-wrap" style="gap: 12px">
              <v-btn small text @click="step = '1'">
                <v-icon left small>mdi-arrow-left</v-icon>
                Change version
              </v-btn>
              <span v-if="selectedVersion" class="font-weight-medium">
                {{ selectedVersion.name }}
              </span>
            </div>
          </v-sheet>

          <v-sheet class="ma-4" outlined rounded>
            <div class="pa-3">
              <issue-selection-table
                v-model="selectedKeys"
                :issues="issues"
                :loading="issuesLoading" />
            </div>
          </v-sheet>

          <v-sheet class="ma-4 pa-3" outlined rounded>
            <v-form ref="form" @submit.prevent="generate">
              <label for="release-name" class="ds-field-label">Release name</label>
              <v-text-field
                id="release-name"
                v-model="releaseName"
                outlined
                dense
                counter="200"
                placeholder="e.g. 1.2.0"
                :rules="nameRules"
                :disabled="generating"></v-text-field>

              <label for="release-instructions" class="ds-field-label">
                Generation instructions (optional)
              </label>
              <v-textarea
                id="release-instructions"
                v-model="instructions"
                outlined
                dense
                auto-grow
                rows="3"
                placeholder="e.g. Keep it short and non-technical; lead with the new dashboard."
                hint="Steers the AI's tone and what it emphasises. Leave empty to use the default tone from Settings."
                persistent-hint
                :disabled="generating"></v-textarea>

              <div class="d-flex align-center flex-wrap mt-4" style="gap: 12px">
                <span class="text--secondary caption">
                  {{ selectedKeys.length }} ticket{{ selectedKeys.length === 1 ? '' : 's' }} will be
                  summarised.
                </span>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  :loading="generating"
                  :disabled="!canGenerate"
                  @click="generate">
                  <v-icon left small>mdi-creation</v-icon>
                  Generate release notes
                </v-btn>
              </div>
            </v-form>
          </v-sheet>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>

    <manual-release-dialog v-model="manualDialog" @created="goToRelease" />
  </v-card>
</template>

<script>
  import formRules from '@Share/config/form-rules';
  import { error, log } from '@/utils/debugLogger';
  import {
    RELEASE_ERROR_MESSAGES,
    isFeatureDisabledError,
    releaseErrorMessage,
  } from '@ReleaseNotes/config/enums';
  import ReleaseNotesDisabledNotice from '@ReleaseNotes/components/ReleaseNotesDisabledNotice.vue';
  import VersionList from '@ReleaseNotes/components/VersionList.vue';
  import IssueSelectionTable from '@ReleaseNotes/components/IssueSelectionTable.vue';
  import ManualReleaseDialog from '@ReleaseNotes/components/ManualReleaseDialog.vue';

  const SEARCH_DEBOUNCE_MS = 400;
  const NAME_MAX_LENGTH = 200;

  export default {
    name: 'JiraVersionPicker',
    components: {
      ReleaseNotesDisabledNotice,
      VersionList,
      IssueSelectionTable,
      ManualReleaseDialog,
    },
    data() {
      return {
        step: '1',
        featureDisabled: false,
        manualDialog: false,
        projectKey: null,
        // Every version is listed by default; the Released/Unreleased buttons narrow it down.
        statusFilter: 'all',
        searchInput: '',
        search: '',
        searchTimer: null,
        options: { page: 1, itemsPerPage: 15 },
        selectedVersion: null,
        selectedKeys: [],
        releaseName: '',
        instructions: '',
        generating: false,
        duplicateReleaseId: null,
        duplicateMessage: RELEASE_ERROR_MESSAGES.RELEASE_ALREADY_EXISTS,
        nameRules: [formRules.required, formRules.lengthMax(NAME_MAX_LENGTH)],
      };
    },
    computed: {
      projects() {
        return this.$store.getters['RELEASE_NOTES/GET_JIRA_PROJECTS'] || [];
      },
      projectsLoading() {
        return this.$store.getters['RELEASE_NOTES/IS_JIRA_PROJECTS_LOADING'];
      },
      versions() {
        return this.$store.getters['RELEASE_NOTES/GET_JIRA_VERSIONS'] || [];
      },
      versionsTotal() {
        return this.$store.getters['RELEASE_NOTES/GET_JIRA_VERSIONS_TOTAL'] || 0;
      },
      versionsLoading() {
        return this.$store.getters['RELEASE_NOTES/IS_JIRA_VERSIONS_LOADING'];
      },
      issues() {
        return this.$store.getters['RELEASE_NOTES/GET_JIRA_ISSUES'] || [];
      },
      issuesLoading() {
        return this.$store.getters['RELEASE_NOTES/IS_JIRA_ISSUES_LOADING'];
      },
      canGenerate() {
        return (
          !this.generating &&
          !!this.selectedVersion &&
          this.selectedKeys.length > 0 &&
          !!this.releaseName.trim()
        );
      },
    },
    watch: {
      projectKey() {
        this.resetPageAndFetch();
      },
      statusFilter() {
        this.resetPageAndFetch();
      },
      searchInput(value) {
        if (this.searchTimer) clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => {
          this.search = value || '';
          this.resetPageAndFetch();
        }, SEARCH_DEBOUNCE_MS);
      },
    },
    methods: {
      /**
       * The Jira project payload is not pinned down by the plan, so the key is used as the
       * display fallback when a project carries no name - the select never renders blank.
       * @param {object} project
       * @returns {string}
       */
      projectText(project) {
        if (!project) return '';
        return project.name || project.key || '';
      },

      /**
       * First load. The feature-flag 404 of §7 is read from the health endpoint, which is probed
       * first and gates the whole screen; the catalogue call below only drives the pre-selection.
       */
      async load() {
        try {
          await this.$store.dispatch('RELEASE_NOTES/FETCH_JIRA_HEALTH');
        } catch (err) {
          if (isFeatureDisabledError(err)) {
            this.featureDisabled = true;
            log('[JiraVersionPicker] Release Notes is disabled server-side.');
            return;
          }
          // Anything else (Jira unreachable, transient failure) has already been toasted;
          // the catalogue call below still runs so the screen is usable if it recovers.
          error('[JiraVersionPicker] Jira health check failed.', err);
        }

        // The action resolves with the paged `{ items, total }` envelope, not a bare array, and it
        // re-throws after toasting — a catalogue failure costs the pre-selection, nothing more.
        let result;
        try {
          result = await this.$store.dispatch('RELEASE_NOTES/FETCH_JIRA_PROJECTS');
        } catch (err) {
          error('[JiraVersionPicker] Could not load the Jira project catalogue.', err);
          return;
        }
        const projects = (result && result.items) || [];
        if (!Array.isArray(projects) || projects.length === 0) return;

        // Prefer the configured default; fall back to the only project when there is just one.
        const defaultKey = await this.defaultProjectKey();
        const match = defaultKey && projects.find((project) => project.key === defaultKey);
        if (match) {
          this.projectKey = match.key;
        } else if (projects.length === 1) {
          this.projectKey = projects[0].key;
        }
      },

      /** The Release Notes settings' default Jira project, or null when settings are unavailable. */
      async defaultProjectKey() {
        try {
          const settings =
            this.$store.getters['RELEASE_NOTES/GET_SETTINGS'] ||
            (await this.$store.dispatch('RELEASE_NOTES/FETCH_SETTINGS'));
          return (settings && settings.defaultJiraProjectKey) || null;
        } catch (err) {
          // A settings failure must not stop the picker: it only costs the pre-selection.
          error('[JiraVersionPicker] Could not read the default Jira project.', err);
          return null;
        }
      },

      fetchVersions() {
        if (!this.projectKey) return;
        this.$store.dispatch('RELEASE_NOTES/FETCH_JIRA_VERSIONS', {
          projectKey: this.projectKey,
          status: this.statusFilter,
          search: this.search,
          page: this.options.page,
          itemsPerPage: this.options.itemsPerPage,
        });
      },

      /**
       * Filter changes always restart at page 1. Reassigning `options` fires the fetch through
       * `onOptionsUpdate`; when the page is already 1 nothing changes, so fetch directly.
       */
      resetPageAndFetch() {
        if (this.options.page !== 1) {
          this.options = { ...this.options, page: 1 };
          this.fetchVersions();
          return;
        }
        this.fetchVersions();
      },

      onOptionsUpdate(opts) {
        const next = opts || {};
        if (next.page === this.options.page && next.itemsPerPage === this.options.itemsPerPage) {
          return;
        }
        this.options = { ...this.options, page: next.page, itemsPerPage: next.itemsPerPage };
        this.fetchVersions();
      },

      onVersionSelected(version) {
        if (!version || version.existingReleaseId) return;
        this.selectedVersion = version;
        this.selectedKeys = [];
        this.releaseName = version.name || '';
        this.duplicateReleaseId = null;
        this.step = '2';
        this.$store.dispatch('RELEASE_NOTES/FETCH_JIRA_VERSION_ISSUES', version.id);
      },

      async generate() {
        if (!this.selectedVersion) return;
        if (!this.selectedKeys.length) {
          this.$dialog.notify.error(RELEASE_ERROR_MESSAGES.RELEASE_NO_ISSUES_SELECTED, {
            position: 'top-right',
            timeout: 4000,
          });
          return;
        }
        if (this.$refs.form && !this.$refs.form.validate()) return;

        this.generating = true;
        this.duplicateReleaseId = null;
        try {
          const result = await this.$store.dispatch('RELEASE_NOTES/GENERATE', {
            jiraVersionId: this.selectedVersion.id,
            name: this.releaseName.trim(),
            issueKeys: this.selectedKeys,
            instructions: this.instructions.trim() || null,
          });
          const releaseId = result ? result.releaseId ?? result.id : null;
          if (!releaseId) {
            error('[JiraVersionPicker] Generate returned no releaseId.', result);
            this.$dialog.notify.error('The release was queued but could not be opened.', {
              position: 'top-right',
              timeout: 5000,
            });
            return;
          }
          this.goToRelease(releaseId);
        } catch (err) {
          // GENERATE re-throws a 409 WITHOUT toasting — a release already exists for this
          // Jira version, so the user is offered a jump to it instead of a blind navigation.
          const existingId =
            err && err.response && err.response.data ? err.response.data.existingReleaseId : null;
          if (existingId) {
            this.duplicateReleaseId = existingId;
            this.duplicateMessage = releaseErrorMessage(
              err,
              RELEASE_ERROR_MESSAGES.RELEASE_ALREADY_EXISTS,
            );
            return;
          }
          error('[JiraVersionPicker] Failed to generate release notes.', err);
        } finally {
          this.generating = false;
        }
      },

      goToRelease(releaseId) {
        if (!releaseId) return;
        this.$router.push({ name: 'release-notes-detail', params: { id: String(releaseId) } });
      },

      backToList() {
        this.$router.push({ name: 'release-notes' });
      },
    },
    mounted() {
      this.load();
    },
    beforeDestroy() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = null;
    },
  };
</script>

<style scoped lang="scss">
  .jira-version-picker__stepper {
    background: transparent !important;
    box-shadow: none !important;
  }

  .jira-version-picker ::v-deep .v-stepper__header {
    box-shadow: none;
  }
</style>
